import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import { ClientContextProvider } from "./contexts/ClientContext";
import "./index.css";

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    // Skip global redirect for sessions.save — the LiveSessionTracker handles
    // auth errors locally (localStorage backup + onComplete nav release).
    // A hard redirect here would fight with the local handler and could lose
    // the recovery banner that Home.tsx shows on next render.
    const mutationKey = event.mutation.options.mutationKey as string[] | undefined;
    const isSaveSession = mutationKey?.some?.(k => typeof k === 'string' && k.includes('sessions'));
    if (!isSaveSession) {
      redirectToLoginIfUnauthorized(error);
    }
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <ClientContextProvider>
        <App />
      </ClientContextProvider>
    </QueryClientProvider>
  </trpc.Provider>
);

// Register Service Worker for PWA with automatic update and hot-reloading
if ('serviceWorker' in navigator) {
  // Listen for the controllerchange event (fired when a new service worker takes over)
  // and reload the page to load the latest bundle immediately.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true;
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(reg => {
        console.log('Service Worker registered successfully.', reg.scope);
        
        // Check for service worker updates immediately on load
        reg.update().catch(() => {});

        // Also check for updates when the window is focused (e.g. returning to the tab)
        window.addEventListener('focus', () => {
          reg.update().catch(() => {});
        });
      })
      .catch(err => console.log('Service Worker registration failed:', err));
  });
}
